import DeviceManager, { onPromiseAttrType } from '../utils/device-manager';

export default function initFocusables() : void {
    const $focusables = document.querySelectorAll<HTMLElement>('.js-focusable');

    $focusables.forEach($focusable => {
        new Focusable($focusable);
    });
}

/**
 * Make element focusable only on specific breakpoints
 * 
 * Usage:
 * <span
 *   class="js-focusable"
 *   data-focusable-breakpoint-gte="xx" // Optional, initialize only if the breakpoint is greater than equal (sm, md, lg, xl)
 *   data-focusable-breakpoint-lt="xx" // Optional, initialize only if the breakpoint is lower than (sm, md, lg, xl)
 * >
 * 
 * This span is focusable in XS, SM, and MD breakpoint
 */
export class Focusable {
    $element: HTMLElement;
    defaultTabIndex: string|null = null;
    breakpointGte: string|null;
    breakpointLt: string|null;
    initialized = false;

    constructor($element: HTMLElement) {
        this.$element = $element;
        this.defaultTabIndex = this.$element.getAttribute('tabindex') || null;
        this.breakpointGte = this.$element.getAttribute('data-focusable-breakpoint-gte') || null;
        this.breakpointLt = this.$element.getAttribute('data-focusable-breakpoint-lt') || null;

        // If the breakpoints doesn't exists, set to null
        if (this.breakpointGte && typeof DeviceManager.devices[this.breakpointGte] === 'undefined') {
            this.breakpointGte = null;
        }
        if (this.breakpointLt && typeof DeviceManager.devices[this.breakpointLt] === 'undefined') {
            this.breakpointLt = null;
        }

        DeviceManager.addCallback((attr: onPromiseAttrType) => {
            if (
                !this.initialized && ( // Is not intiialized
                    ( // Breakpoints not defined
                        !this.breakpointGte && !this.breakpointLt
                    ) ||
                    ( // Breakpoints defined and current is between them
                        this.breakpointGte && this.breakpointLt &&
                        DeviceManager.isBetween(attr.current, this.breakpointGte, this.breakpointLt)
                    ) ||
                    ( // Breakpoint GTE defined and current is GTE it
                        this.breakpointGte && !this.breakpointLt &&
                        DeviceManager.isGreaterThanOrEqual(attr.current, this.breakpointGte)
                    ) ||
                    ( // Breakpoint LT defined and current is LT it
                        !this.breakpointGte && this.breakpointLt &&
                        DeviceManager.isLowerThan(attr.current, this.breakpointLt)
                    )
                )
            ) {
                this.initialized = true;

                this.init();
            } else if (
                // Is initialized and breakpoints defined
                this.initialized && (this.breakpointGte || this.breakpointLt) && (
                    ( // Breakpoints defined and current is not between them
                        this.breakpointGte && this.breakpointLt &&
                        !DeviceManager.isBetween(attr.current, this.breakpointGte, this.breakpointLt)
                    ) ||
                    ( // Breakpoint GTE defined and current is not GTE it
                        this.breakpointGte && !this.breakpointLt &&
                        !DeviceManager.isGreaterThanOrEqual(attr.current, this.breakpointGte)
                    ) ||
                    ( // Breakpoint LT defined and current is not LT it
                        !this.breakpointGte && this.breakpointLt &&
                        !DeviceManager.isLowerThan(attr.current, this.breakpointLt)
                    )
                )
            ) {
                this.initialized = false;

                this.destroy();
            }
        });
    }

    /**
     * Make element focusable
     */
    init() : void {
        this.$element.setAttribute('tabindex', '0');
    }

    /**
     * Reset element focusable state
     */
    destroy() : void {
        if (this.defaultTabIndex) {
            this.$element.setAttribute('tabindex', this.defaultTabIndex);
        } else {
            this.$element.removeAttribute('tabindex');
        }
    }
}
